import { useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import moment from "moment";
import momentTz from "moment-timezone";

// import Components
import Button from "components/atoms/Button/Button";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import DatePicker from "components/atoms/DatePicker/DatePicker";
import TimeInput from "components/atoms/TimeInput/TimeInput";
import { getScheduleOption } from "api";
import errorToast from "helpers/errorToast";

interface PickupDeliveryTimeProps {
  location: string;
}

function PickupDeliveryTime(props: PickupDeliveryTimeProps) {
  const { location } = props;
  const intl = useIntl();
  const methods = useFormContext();
  const { getValues, unregister } = methods;

  const [showSpecifyPickup, setShowSpecifyPickup] = useState(false);
  const [showSpecifyDelivery, setShowSpecifyDelivery] = useState(false);
  const [allowAsapDelivery, setAllowAsapDelivery] = useState(false);

  useEffect(() => {
    getScheduleOption(location)
      .then((res) => setAllowAsapDelivery(res.data.schedule_options.allow_asap_delivery))
      .catch((err) => errorToast(err));
  }, [location]);

  const cargoReadyPickupOption = [
    {
      label: intl.formatMessage({ id: "new-request.schedule.cargoReadyPickupOption.after", defaultMessage: "After" }),
      value: "after",
    },
    {
      label: intl.formatMessage({ id: "new-request.schedule.cargoReadyPickupOption.between", defaultMessage: "Between" }),
      value: "between",
    },
  ];

  const cargoReadyDeliveryOption = [
    {
      label: intl.formatMessage({
        id: "new-request.schedule.defaultCargoReadyDeliveryOption.before",
        defaultMessage: "Before",
      }),
      value: "before",
    },
    {
      label: intl.formatMessage({
        id: "new-request.schedule.defaultCargoReadyDeliveryOption.between",
        defaultMessage: "Between",
      }),
      value: "between",
    },
    ...(allowAsapDelivery
      ? [
          {
            label: intl.formatMessage({
              id: "new-request.schedule.defaultCargoReadyDeliveryOption.asap",
              defaultMessage: "ASAP Delivery",
            }),
            value: "asap_delivery",
          },
        ]
      : []),
  ];

  function handleCargoReadyChange(type: string, value: string) {
    if (type === "pickup" && value === "after") {
      unregister("schedule.pickup_before_date");
      unregister("schedule.pickup_before_time");
    } else if (value === "before" && type === "delivery") {
      unregister("schedule.stop1_before_date");
      unregister("schedule.stop1_before_time");
    } else if (value === "asap_delivery") {
      unregister("schedule.stop1_after_date");
      unregister("schedule.stop1_after_time");
      unregister("schedule.stop1_before_date");
      unregister("schedule.stop1_before_time");
      unregister("schedule.stop1_timezone");
    }
  }

  const getFormattedDateTime = (key: string, timezoneKey: string) => {
    const dateKey = `${key}_date`;
    const timeKey = `${key}_time`;

    return momentTz.tz(`${getValues(dateKey)} ${getValues(timeKey)}`, getValues(timezoneKey));
  };

  const isValidPickupHours = (value: any): boolean =>
    moment(value, "HH:mm:ss").isAfter(moment(getValues("schedule.pickup_hours_from"), "HH:mm:ss"));

  const isValidDeliveryHours = (value: any): boolean =>
    moment(value, "HH:mm:ss").isAfter(moment(getValues("schedule.delivery_hours_from"), "HH:mm:ss"));

  const isValidDeliveryDate = () => {
    const deliveryTime = getFormattedDateTime("schedule.stop1_after", "schedule.stop1_timezone");
    const pickupTime = getFormattedDateTime("schedule.pickup_ready", "schedule.pickup_timezone");
    return deliveryTime.isAfter(pickupTime);
  };

  const isValidPickupBeforeDate = () => {
    const pickupBeforeTime = getFormattedDateTime("schedule.pickup_before", "schedule.pickup_timezone");
    const pickupReadyTime = getFormattedDateTime("schedule.pickup_ready", "schedule.pickup_timezone");
    return pickupBeforeTime.isAfter(pickupReadyTime);
  };

  const isValidDeliveryBeforeDate = () => {
    const deliveryBeforeTime = getFormattedDateTime("schedule.stop1_before", "schedule.stop1_timezone");
    const deliveryAfterTime = getFormattedDateTime("schedule.stop1_after", "schedule.stop1_timezone");
    return deliveryBeforeTime.isAfter(deliveryAfterTime);
  };

  return (
    <div>
      <Row className="mb-15">
        <Col lg={2} md={2}>
          <p className="template-section-title">
            <FormattedMessage id="new-request.schedule.pickupPlanning" defaultMessage="Pickup planning" />
          </p>
        </Col>
        <Col lg={3} md={4} className="mb-15 mb-lg-0">
          <label>Pickup</label>
          <Dropdown
            useHookForm
            name="schedule.pickup_cargo_ready"
            className="schedule-dropdown"
            defaultValue={getValues("schedule.pickup_cargo_ready") || "after"}
            options={cargoReadyPickupOption}
            onChange={(val: any) => {
              handleCargoReadyChange("pickup", val?.value);
            }}
          />
        </Col>
        <Col lg={6} md={6} className="mb-15 mb-lg-0">
          <div>
            <label className="required">
              <FormattedMessage id="new-request.schedule.cargoPickupAfterDate" defaultMessage="Pickup after date" />
            </label>
          </div>
          <div className="d-flex">
            <DatePicker
              name="schedule.pickup_ready_date"
              wrapperClassNames="mr-2"
              required
              registerOptions={{ deps: ["schedule.stop1_after_date", "schedule.pickup_before_date"] }}
            />
            <TimeInput
              name="schedule.pickup_ready_time"
              required
              defaultValue="09:00:00"
              registerOptions={{
                deps: ["schedule.stop1_after_date", `schedule.pickup_before_date`],
              }}
            />
          </div>
          {getValues("schedule.pickup_cargo_ready") === "between" && (
            <Row className="pt-15">
              <Col sm={12} className="mb-15 mb-lg-0">
                <div className="pb-2 required label font-b3">
                  <FormattedMessage id="new-request.schedule.pickupBeforeDate" defaultMessage="Pickup before date" />
                </div>
                <div className="d-flex">
                  <DatePicker
                    name="schedule.pickup_before_date"
                    wrapperClassNames="mr-2"
                    required
                    validations={{
                      isValidPickupBeforeDate: () =>
                        isValidPickupBeforeDate() ||
                        intl.formatMessage({
                          id: "new-request.schedule.pickupBeforeDateErrorMessage",
                          defaultMessage: "Pickup before date must be after pickup ready date",
                        }),
                    }}
                  />
                  <TimeInput
                    name="schedule.pickup_before_time"
                    required
                    registerOptions={{
                      deps: `schedule.pickup_before_date`,
                    }}
                  />
                </div>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
      <Row>
        <Col>
          <div>
            {showSpecifyPickup || getValues("schedule.pickup_hours_from") || getValues("schedule.pickup_hours_to") ? (
              <>
                <Row className="mb-15">
                  <Col sm={2} />
                  <Col lg={4}>
                    <Button
                      variant="primary"
                      className="rounded-pill blue-200 h-26"
                      label={intl.formatMessage({
                        id: "new-request.schedule.dimissPickHours",
                        defaultMessage: "Dismiss pickup hours",
                      })}
                      onClick={() => {
                        setShowSpecifyPickup(false);
                        unregister("schedule.pickup_hours_from");
                        unregister("schedule.pickup_hours_to");
                      }}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={2} />
                  <Col lg={6} md={7} className="mb-15 mb-md-0">
                    <div className="d-flex">
                      <div className="mr-3">
                        <label className="required">
                          <FormattedMessage id="new-request.schedule.specifyHoursFrom" defaultMessage="From" />
                        </label>
                        <TimeInput
                          name="schedule.pickup_hours_from"
                          defaultValue="09:00:00"
                          registerOptions={{ deps: "schedule.pickup_hours_to" }}
                          required
                        />
                      </div>
                      <div>
                        <label className="required">
                          <FormattedMessage id="new-request.schedule.specifyHoursTo" defaultMessage="To" />
                        </label>
                        <TimeInput
                          name="schedule.pickup_hours_to"
                          defaultValue="18:00:00"
                          required
                          validations={{
                            isValidPickupHours: (v: any) =>
                              isValidPickupHours(v) ||
                              intl.formatMessage({
                                id: "new-request.schedule.specifyPickupHourErrorMessage",
                                defaultMessage: "End time should be greater than start time",
                              }),
                          }}
                        />
                      </div>
                    </div>
                  </Col>
                </Row>
              </>
            ) : (
              <Row>
                <Col md={2} />
                <Col md={4}>
                  <Button
                    variant="primary"
                    className="rounded-pill blue-200 h-26"
                    label={intl.formatMessage({
                      id: "new-request.schedule.addPickupSpecifyHour",
                      defaultMessage: "Add pickup hours",
                    })}
                    onClick={() => setShowSpecifyPickup(true)}
                  />
                </Col>
              </Row>
            )}

            <hr />

            <Row className="mb-15">
              <Col md={2} lg={2}>
                <p className="template-section-title">
                  <FormattedMessage id="new-request.schedule.deliveryPlanning" defaultMessage="Delivery planning" />
                </p>
              </Col>
              <Col lg={3} md={4} className="mb-15 mb-md-0">
                <label>Delivery</label>
                <Dropdown
                  useHookForm
                  name="schedule.delivery_cargo_ready"
                  className="schedule-dropdown"
                  defaultValue={getValues("schedule.delivery_cargo_ready") || "before"}
                  options={cargoReadyDeliveryOption}
                  onChange={(val: any) => {
                    handleCargoReadyChange("delivery", val?.value);
                  }}
                />
              </Col>
              {getValues("schedule.delivery_cargo_ready") !== "asap_delivery" && (
                <Col lg={6} md={6} className="mb-15 mb-md-0 mt-1">
                  {getValues("schedule.delivery_cargo_ready") === "before" ? (
                    <div className="pb-2 required label font-b3">
                      <FormattedMessage id="new-request.schedule.deliveryBeforeDate" defaultMessage="Delivery before date" />
                    </div>
                  ) : (
                    <div className="pb-2 required label font-b3">
                      <FormattedMessage id="new-request.schedule.deliveryAfterDate" defaultMessage="Delivery after date" />
                    </div>
                  )}
                  <div className="d-flex">
                    <DatePicker
                      name="schedule.stop1_after_date"
                      wrapperClassNames="mr-2"
                      required
                      validations={{
                        isValidDeliveryDate: () =>
                          isValidDeliveryDate() ||
                          intl.formatMessage({
                            id: "new-request.schedule.deliveryReadyDateErrorMessage",
                            defaultMessage: "Delivery ready date must be after pickup ready date",
                          }),
                      }}
                      registerOptions={{
                        deps: ["schedule.stop1_before_date"],
                      }}
                    />
                    <TimeInput
                      name="schedule.stop1_after_time"
                      required
                      defaultValue="17:00:00"
                      registerOptions={{
                        deps: ["schedule.stop1_after_date", "schedule.stop1_before_date"],
                      }}
                    />
                  </div>
                  {getValues("schedule.delivery_cargo_ready") === "between" && (
                    <Row className="pt-15">
                      <Col lg={6} md={7} className="mb-15 mb-lg-0">
                        <div className="pb-2 required label font-b3">
                          <FormattedMessage
                            id="new-request.schedule.deliveryBeforeDate"
                            defaultMessage="Delivery before date"
                          />
                        </div>
                        <div className="d-flex">
                          <DatePicker
                            name="schedule.stop1_before_date"
                            wrapperClassNames="mr-2"
                            required
                            validations={{
                              isValidDeliveryBeforeDate: () =>
                                isValidDeliveryBeforeDate() ||
                                intl.formatMessage({
                                  id: "new-request.schedule.deliveryBeforeDateErrorMessage",
                                  defaultMessage: "Delivery before date must be after delivery ready date",
                                }),
                            }}
                          />
                          <TimeInput
                            name="schedule.stop1_before_time"
                            required
                            registerOptions={{
                              deps: `schedule.stop1_before_date`,
                            }}
                          />
                        </div>
                      </Col>
                    </Row>
                  )}
                </Col>
              )}
            </Row>
            <Row>
              <Col>
                <div>
                  {showSpecifyDelivery ||
                  getValues("schedule.delivery_hours_from") ||
                  getValues("schedule.delivery_hours_to") ? (
                    <>
                      <Row className="mb-15">
                        <Col md={2} />
                        <Col lg={4}>
                          <Button
                            variant="primary"
                            className="rounded-pill blue-200 h-26"
                            label={intl.formatMessage({
                              id: "new-request.schedule.dimissDeliveryHours",
                              defaultMessage: "Dismiss delivery hours",
                            })}
                            onClick={() => {
                              setShowSpecifyDelivery(false);
                              unregister("schedule.delivery_hours_from");
                              unregister("schedule.delivery_hours_to");
                            }}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col md={2} />
                        <Col lg={6} md={7} className="mb-15 mb-md-0">
                          <div className="d-flex">
                            <div className="mr-3">
                              <label className="required">
                                <FormattedMessage id="new-request.schedule.specifyHoursFrom" defaultMessage="From" />
                              </label>
                              <TimeInput
                                name="schedule.delivery_hours_from"
                                defaultValue="09:00:00"
                                registerOptions={{ deps: "schedule.delivery_hours_to" }}
                                required
                              />
                            </div>
                            <div>
                              <label className="required">
                                <FormattedMessage id="new-request.schedule.specifyHoursTo" defaultMessage="To" />
                              </label>
                              <TimeInput
                                name="schedule.delivery_hours_to"
                                defaultValue="18:00:00"
                                required
                                validations={{
                                  isValidDeliveryHours: (v: any) =>
                                    isValidDeliveryHours(v) ||
                                    intl.formatMessage({
                                      id: "new-request.schedule.specifyDeliveryHourErrorMessage",
                                      defaultMessage: "End time should be greater than start time",
                                    }),
                                }}
                              />
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </>
                  ) : (
                    <Row>
                      <Col md={2} />
                      <Col md={4}>
                        <Button
                          variant="primary"
                          className="rounded-pill blue-200 h-26"
                          label={intl.formatMessage({
                            id: "new-request.schedule.addDeliverySpecifyHour",
                            defaultMessage: "Add delivery hours",
                          })}
                          onClick={() => setShowSpecifyDelivery(true)}
                        />
                      </Col>
                    </Row>
                  )}
                  <hr />
                </div>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default PickupDeliveryTime;
